import React, { useContext, useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";
import {
  Button,
  DateTimeController,
  Grid,
  Icons,
  Loader,
  PanelModal,
  Toast,
} from "@pushpress/shared-components";
import { AppContext } from "../../../context/app";
import { defaultToastOptions, extractError } from "../../../utils/misc";
import { removeSecondsAndTimezone } from "../../../utils/dateUtils";
import { useAppointmentQuery } from "../../../apis/appointmentApi.hooks";
import {
  PostRescheduleAppointmentPayload,
  postRescheduleAppointment,
} from "../../../apis/appointmentApi";
import { useFeatureFlag } from "../../../context/features";
import { RECURRING_APPT_FEATURE_FLAG } from "../../../context/features/features.utils";

interface RescheduleForm {
  date: string;
}

interface AppointmentRescheduleModalProps {
  appointmentUuid?: string;
  visible: boolean;
  onClose: VoidFunction;
  onSuccess: () => void;
}

function AppointmentRescheduleModal(props: AppointmentRescheduleModalProps) {
  const { visible, onClose } = props;

  return (
    <PanelModal
      width="400px"
      visible={visible}
      closeModal={onClose}
      disableClickAway
      disableEscapeKeyDown
      verticalAlign="top"
    >
      <AppointmentRescheduleModalContent {...props} />
    </PanelModal>
  );
}

function AppointmentRescheduleModalContent(
  props: AppointmentRescheduleModalProps,
) {
  const { appointmentUuid, onClose, onSuccess } = props;

  const { isFeatureEnabled } = useFeatureFlag();

  const recurringBookingFlag = isFeatureEnabled(RECURRING_APPT_FEATURE_FLAG);

  const app = useContext(AppContext);

  const { data } = useAppointmentQuery(appointmentUuid!, {
    enabled: !!appointmentUuid,
  });

  const postRescheduleMutation = useMutation(postRescheduleAppointment);

  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    formState: { isValid },
  } = useForm<RescheduleForm>({
    mode: "onTouched",
  });

  const { date } = useWatch({
    control,
  });

  const save = async (values: RescheduleForm) => {
    if (!appointmentUuid) {
      throw new Error("appointmentUuid is empty");
    }
    if (!data?.startTime || !data?.endTime) {
      throw new Error("Invalid start or end time");
    }
    const timeZone = app.client!.timezone;
    const startTime = new Date(`${values.date}:00.000Z`);
    const duration =
      new Date(data.endTime).getTime() - new Date(data.startTime).getTime();
    const endTime = new Date(startTime.getTime() + duration);
    const payload: PostRescheduleAppointmentPayload = {
      startTime,
      endTime,
      startTimeZone: timeZone,
      endTimeZone: timeZone,
    };
    try {
      await postRescheduleMutation.mutateAsync({
        clientUuid: app.client!.uuid,
        appointmentUuid,
        payload,
      });
      toast.success("Appointment rescheduled", defaultToastOptions);
      onSuccess();
    } catch (error) {
      toast.error(extractError(error), defaultToastOptions);
    }
  };

  const handleOk = handleSubmit(save);

  useEffect(() => {
    if (data?.startTime) {
      const date = removeSecondsAndTimezone(data?.startTime);
      setValue("date", date);
      trigger("date");
    }
  }, [data, setValue, trigger]);

  if (!data) {
    return (
      <Grid container>
        <Grid item xs={12} container justifyContent="center" paddingY={4}>
          <Loader />
        </Grid>
      </Grid>
    );
  }

  const okLoading = postRescheduleMutation.isLoading;
  const okDisabled =
    !isValid || removeSecondsAndTimezone(data?.startTime) === date;

  return (
    <>
      <PanelModal.Header title="Reschedule">
        <Button
          text="Close"
          buttonType="secondary"
          size="medium"
          onClick={onClose}
        />
        <Button
          text="OK"
          buttonType="primary"
          size="medium"
          disabled={okDisabled}
          loading={okLoading}
          onClick={handleOk}
        />
      </PanelModal.Header>
      <PanelModal.Body paddingSize="none">
        <div className="pt-4 pr-4 pb-0 pl-4">
          <form onSubmit={handleOk}>
            <DateTimeController
              control={control}
              name="date"
              label="When"
              required
              DateInputProps={{ startIcon: Icons.Classes }}
              TimeInputProps={{ startIcon: Icons.Clock, minuteStep: 5 }}
            />
          </form>
          {recurringBookingFlag && (
            <Toast
              icon={Icons.Information}
              iconColor="success"
              message="Only this appointment will be updated"
            />
          )}
        </div>
      </PanelModal.Body>
    </>
  );
}

export default AppointmentRescheduleModal;
